import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCreateCertificateMutation,
  useLazyGetCertificateByIdQuery,
  useUpdateCertificateMutation
} from '@modules/certificate';
import { useLazyGetCategoriesQuery } from '@modules/category';
import { useRenderFileViewHook } from '@app/hooks/imagesRender';
import { MESSAGES } from '@app/constant';
import { VisibleEnum } from 'types';

import type { ICreateCertificateFormData } from './types';
import type { ICategory } from '@modules/category/types';

import { createCertificateSchema, initialFormState, updateCertificateSchema } from './data';
import { handleDeleteImage, onChangeUpload } from './localHelper';

export interface LocalState {
  isSuccess: string;
  largeImageMessage: string;
  isImageViewOpen: boolean;
  isButtonLoading: boolean;
  imageSrcForAction: string;
  isDeleteModalOpen: boolean;
}

const initialLocalState: LocalState = {
  isSuccess: '',
  largeImageMessage: '',
  isImageViewOpen: false,
  isButtonLoading: false,
  imageSrcForAction: '',
  isDeleteModalOpen: false,
};

export const useMutationCertificatesLogic = () => {
  const { certificateId = '' } = useParams();
  const [getCategories] = useLazyGetCategoriesQuery();
  const [createCertificate] = useCreateCertificateMutation();
  const [fetchCertificateById] = useLazyGetCertificateByIdQuery();
  const [updateCertificateById] = useUpdateCertificateMutation();

  const [categories, setCategories] = useState<ICategory[]>([]);
  const [state, setState] = useState<LocalState>(initialLocalState);

  const schema = useMemo(
    () => yupResolver(certificateId ? updateCertificateSchema : createCertificateSchema),
    [certificateId]
  );

  const methods = useForm<ICreateCertificateFormData>({
    // @ts-ignore
    resolver: schema,
    mode: 'onSubmit',
    defaultValues: initialFormState,
  });

  const {
    reset,
    watch,
    register,
    setValue,
    setError,
    handleSubmit,
    formState: { errors },
  } = methods;

  const pdf = watch('pdf');
  const images = watch('images');
  const category = watch('category');
  const updateImages = watch('updateCertificateImagesFromBackend');
  const previews = useRenderFileViewHook(images);

  const pdfFile = updateImages?.find((file) => file.url.endsWith('.pdf'));
  const otherFile = updateImages?.find((file) => !file.url.endsWith('.pdf'));

  const buildFormData = (data: ICreateCertificateFormData): FormData => {
    const formData = new FormData();
    const allFiles = new DataTransfer();

    data.pdf?.[0] && allFiles.items.add(data.pdf[0]);
    data.images?.[0] && allFiles.items.add(data.images[0]);

    formData.append('titleEn', data.titleEn);
    formData.append('titleRu', data.titleRu);
    formData.append('titleAm', data.titleAm);
    formData.append('visible', data.visible);
    formData.append('categoryIds', JSON.stringify(data.category));

    for (let i = 0; i < allFiles.files.length; i++) {
      formData.append('files[]', allFiles.files[i])
    }

    return formData;
  };

  const onSubmit = async (data: ICreateCertificateFormData) => {
    setState((prev) => ({ ...prev, isButtonLoading: true, isSuccess: '' }));

    try {
      if (!certificateId && !data.images?.length) {
        throw new Error('Images are required');
      }

      const formData = buildFormData(data);

      certificateId
        ? await updateCertificateById({ id: certificateId, formData }).unwrap()
        : await createCertificate(formData).unwrap();

      if (!certificateId) {
        reset();
      }

      setState((prev) => ({
        ...prev,
        isButtonLoading: false,
        largeImageMessage: '',
        isSuccess: certificateId
          ? MESSAGES.certificateHasBeenSuccessfullyUpdated
          : MESSAGES.certificateHasBeenSuccessfullyCreated
      }));
    } catch (error: any) {
      console.error('Submit Error:', error);

      setState((prev) => ({
        ...prev,
        isButtonLoading: false,
        largeImageMessage: '',
        isSuccess: error?.data?.message || MESSAGES.somethingWentWrong,
      }));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [certificateRes, categoriesRes] = await Promise.all([
          fetchCertificateById(certificateId),
          getCategories({ offset: 0, limit: 500 }),
        ]);

        const cert = certificateRes.data;
        const catData = categoriesRes.data?.certificateCategory;

        if (catData) {
          setCategories(catData);
        }

        if (cert) {
          const parsedCategories = JSON.parse(cert.categoryIds || '[]');

          reset({
            titleEn: cert.titleEn || '',
            titleRu: cert.titleRu || '',
            titleAm: cert.titleAm || '',
            category: parsedCategories,
            updateCertificateImagesFromBackend: cert.images,
            visible: cert.visible ? VisibleEnum.YES : VisibleEnum.NO,
          });
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, [certificateId]);

  return {
    ...state,
    certificateId,
    pdf,
    pdfFile,
    otherFile,
    category,
    methods,
    images,
    errors,
    previews,
    categories,
    updateCertificateImagesFromBackend: updateImages,
    watch,
    setState,
    onSubmit,
    register,
    handleSubmit,
    setValue,
    handleDeleteImage: (base64: string) =>
      handleDeleteImage({ base64, previews, setValue, setState, images }),
    onChangeUpload: (e: ChangeEvent<HTMLInputElement>, isPdf?: boolean) =>
      onChangeUpload({ event: e, isPdf, images, setValue, setState, setError }),
  };
};
